import { useState, useEffect } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Modal, { ModalControlProps } from "../../layouts/Modal";
import { Button } from "../base/Button";
import LoadingWheel from "../shared/LoadingWheel";
import { Alert } from "../base/Alert";
import useCustomers from "../../data/Customer/useCustomers";
import useReportCustomerLinks from "../../data/Report/useReportCustomerLinks";
import useReportMutations from "../../data/Report/useReportMutations";
import { CustomerReportLink, ICustomerReport, IUpdateCustomerReportLinks } from "../../models/Reports/CustomerReportLink";
import { errorResolver } from "../../utils/errorResolver";
import useCustomerReports from "../../data/Report/useCustomerReports";

interface ReportLinksModalProps extends ModalControlProps {
  reportId: string;
  reportTitle: string;
}

export function ReportLinksModal({ open, setOpen, reportId, reportTitle }: ReportLinksModalProps) {
  const { customers } = useCustomers();
  const { reportCustomerLinks } = useReportCustomerLinks(reportId);
  const { updateCustomerReportLinks } = useReportMutations();
  
  const [selectedCustomers, setSelectedCustomers] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize selected customers when data loads
  useEffect(() => {
    if (customers.data && reportCustomerLinks.data) {
      const linkedCustomerIds = new Set(
        reportCustomerLinks.data.map(cr => cr.customerId)
      );
      setSelectedCustomers(linkedCustomerIds);
    }
  }, [customers.data, reportCustomerLinks.data]);

  const handleCustomerToggle = (customerId: string) => {
    const newSelected = new Set(selectedCustomers);
    if (newSelected.has(customerId)) {
      newSelected.delete(customerId);
    } else {
      newSelected.add(customerId);
    }
    setSelectedCustomers(newSelected);
  };

  const handleSelectAll = () => {
    if (customers.data) {
      const allCustomerIds = customers.data.map(c => c.id);
      setSelectedCustomers(new Set(allCustomerIds));
    }
  };

  const handleDeselectAll = () => {
    setSelectedCustomers(new Set());
  };

  const handleSave = async () => {
    if (!customers.data) return;

    setIsSubmitting(true);
    
    const links: CustomerReportLink[] = Array.from(selectedCustomers).map(customerId => ({
      customerId,
      reportId
    }));

    updateCustomerReportLinks.mutate(
      { links, reportId },
      {
        onSettled: () => {
          setIsSubmitting(false);
        },
        onSuccess: () => {
          setOpen(false);
        }
      }
    );
  };

  const getCustomerName = (customerId: string) => {
    return customers.data?.find(c => c.id === customerId)?.name || "Unknown Customer";
  };

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-2xl">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          Manage Customer Links for "{reportTitle}"
        </h3>
        <button
          type="button"
          className="rounded-md bg-white text-gray-400 hover:text-gray-500"
          onClick={() => setOpen(false)}
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {customers.isLoading || reportCustomerLinks.isLoading ? (
        <div className="py-8">
          <LoadingWheel />
        </div>
      ) : customers.isError ? (
        <Alert
          severity="error"
          title="Error loading customers"
          message={errorResolver(customers.error)}
        />
      ) : reportCustomerLinks.isError ? (
        <Alert
          severity="error"
          title="Error loading customer reports"
          message={errorResolver(reportCustomerLinks.error)}
        />
      ) : (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Select customers that should have access to this report
            </p>
            <div className="flex space-x-2">
              <Button
                variant="outlined"
                size="sm"
                onClick={handleSelectAll}
              >
                Select All
              </Button>
              <Button
                variant="outlined"
                size="sm"
                onClick={handleDeselectAll}
              >
                Deselect All
              </Button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto border rounded-lg">
            {customers.data?.map((customer) => (
              <div
                key={customer.id}
                className="flex items-center px-4 py-3 border-b last:border-b-0 hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  id={`customer-${customer.id}`}
                  checked={selectedCustomers.has(customer.id)}
                  onChange={() => handleCustomerToggle(customer.id)}
                  className="rounded border-orange-600 focus:ring-orange-600"
                />
                <label
                  htmlFor={`customer-${customer.id}`}
                  className="ml-3 flex-1 cursor-pointer"
                >
                  <div className="font-medium text-gray-900">{customer.name}</div>
                  <div className="text-sm text-gray-500">{customer.domain}</div>
                </label>
              </div>
            ))}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button
              variant="outlined"
              onClick={() => setOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
